/**
 * @fileoverview 图片序列加载与渲染工具库
 * 
 * 本模块提供了一套完整的图片序列（视频帧）加载、缓存和渲染解决方案，
 * 特别适用于基于滚动的视频动画效果（Scroll-triggered Video Animation）。
 * 
 * ## 核心组件
 * 
 * ### 1. CloudinaryProvider - 数据提供者
 * **职责**：负责从 Cloudinary CDN 生成图片序列的 URL
 * - 解析 Cloudinary 视频 URL
 * - 根据帧索引计算时间戳并生成对应的图片 URL
 * - 提供总帧数信息
 * 
 * ### 2. FrameLoader - 帧加载器
 * **职责**：管理图片序列的加载、缓存和预加载策略
 * - 滑动窗口缓存策略：仅保留当前帧前后约 80 帧（约 4 秒内容）
 * - 双重缓存机制：浏览器 HTTP 缓存 + JavaScript Map 对象缓存
 * - 智能预加载：根据滚动位置动态调整缓存窗口
 * - 内存管理：自动清理超出窗口的帧，防止内存泄漏
 * 
 * ### 3. CanvasRenderer - Canvas 渲染器
 * **职责**：负责在 Canvas 上渲染图片序列，支持按需渲染和 DPR 适配
 * - **按需渲染**：不使用 requestAnimationFrame 循环，只在进度变化时绘制
 * - **无插值动画**：离散帧序列直接绘制，避免 Lerp 导致的帧闪烁
 * - DPR 适配：自动处理设备像素比，保证高清屏显示效果
 * - 响应式：支持窗口大小变化时自动调整 Canvas 尺寸并重绘当前帧
 * - 渲染优化：帧索引不变时跳过绘制，减少不必要的渲染
 * - 视口管理：离开视口时清理缓存，进入视口时恢复缓存
 * - **懒加载**：内置基于视口距离的懒加载机制，无需外部手动管理
 * - **刷新恢复**：页面刷新后能自动恢复当前进度帧或默认帧（最后一帧）
 * 
 * ## 组件关系
 * 
 * ```
 * ┌─────────────────┐
 * │    Provider     │ ← 数据源：生成帧 URL
 * └────────┬────────┘
 *          │ 提供 URL
 *          ▼
 * ┌─────────────────┐
 * │   FrameLoader   │ ← 加载管理：缓存策略、预加载
 * └────────┬────────┘
 *          │ 提供 Image 对象
 *          ▼
 * ┌─────────────────┐
 * │ CanvasRenderer  │ ← 渲染调度：Canvas 绘制、懒加载、状态恢复
 * └────────┬────────┘
 *          │ 渲染到
 *          ▼
 * ┌─────────────────┐
 * │HTMLCanvasElement│
 * └─────────────────┘
 * ```
 * 
 * ## 典型使用场景：结合 JxsScrollTrigger
 * 
 * ```javascript
 * import { FrameLoader, CanvasRenderer, CloudinaryProvider } from '@jxs/image-sequence';
 * import { JxsScrollTrigger } from '@jxs/gsap';
 * 
 * // 1. 创建数据提供者
 * const provider = new CloudinaryProvider(cloudinaryUrl, {
 *   duration: 5
 * });
 * 
 * // 2. 创建帧加载器
 * const frameLoader = new FrameLoader(provider); // 缓存 80 帧
 * 
 * // 3. 创建 Canvas 渲染器
 * const canvas = document.querySelector('canvas');
 * const renderer = new CanvasRenderer(canvas, frameLoader);
 * 
 * // 4. 启用懒加载（关键步骤）
 * // waitForReady() 会自动监听滚动事件，当元素距离视口 600px 内时开始加载
 * renderer.waitForReady();
 * 
 * // 5. 创建 ScrollTrigger 控制滚动进度
 * const scroller = new JxsScrollTrigger(viewport, {
 *   trigger: viewport,
 *   distance: 3000, // 滚动距离
 *   scrub: 0
 * });
 * 
 * // 6. 绑定滚动进度到渲染器（推荐方式）
 * scroller.on('progress', (progress) => {
 *   renderer.render(progress); // progress: 0~1
 * });
 * 
 * // 7. 窗口大小变化处理，建议套用 debounce
 * window.addEventListener('resize', () => {
 *   renderer.resize(); // 重新计算 Canvas 尺寸并重绘当前帧
 * });
 * 
 * // 8. 组件销毁时清理
 * function destroy() {
 *   renderer.destroy();
 *   frameLoader.destroy();
 *   scroller.destroy();
 * }
 * ```
 * 
 * 
 * ## 注意事项
 * 
 * 1. **必须调用 waitForReady()**：如果不显式调用，图片不会自动加载
 * 2. **destroy 时必须清理**：调用 renderer.destroy() 会同时清理 scroll 监听器和 inView 观察者
 * 3. **resize 时机**：resize() 会在满足加载条件时自动调用，无需手动干预
 * 4. **避免 invalidateOnRefresh**：GSAP ScrollTrigger 的 invalidateOnRefresh:true 会导致 Timeline 重置，应避免使用
 * 
 */

import { jxsAnimate } from '@jxs/gsap';
import { debounce } from '@jxs/utilities';

/**
 * @typedef {Object} ImageSequenceProvider
 * @property {(index: number) => string} getFrameUrl - 获取指定帧的 URL
 * @property {() => number} getTotalFrames - 获取总帧数
 */

/**
 * @typedef {Object} CloudinaryProviderConfig
 * @property {string} cloudName - Cloudinary 空间名称
 * @property {string} videoPublicId - 视频 public ID
 * @property {number} duration - 视频时长（秒）
 * @property {number} fps - 每秒帧数
 * @property {number} width - 输出图片宽度
 * @property {string} format - 输出格式（webp, jpg 等）
 */

/**
 * Cloudinary 图片序列提供者
 * 负责配置解析、帧URL生成
 */
export class CloudinaryProvider {
  /**
   * @param {string} url
   * @param {Partial<CloudinaryProviderConfig>} config
   */
  constructor(url, config) {

    const match = url.match(/res\.cloudinary\.com\/([^\/]+)\/video\/upload\/(.+)\.(mp4|webm|mov|m4v)/i);

    if (!match) {
      console.error('Invalid Cloudinary URL');
    }

    /** @type {CloudinaryProviderConfig} */
    this.config = {
      // @ts-ignore
      cloudName: match[1],
      // @ts-ignore
      videoPublicId: match[2],
      duration: 5,
      fps: 20,
      width: 1920,
      format: 'webp',
      ...config,
    };
  }

  /**
   * 获取指定帧的 URL
   * @param {number} index - 帧索引
   * @returns {string}
   */
  getFrameUrl(index) {
    const time = (index / this.config.fps).toFixed(2);
    return `https://res.cloudinary.com/${this.config.cloudName}/video/upload/so_${time},w_${this.config.width},q_auto,f_${this.config.format}/${this.config.videoPublicId}.${this.config.format}`;
  }

  /**
   * 获取总帧数
   * @returns {number}
   */
  getTotalFrames() {
    return Math.ceil(this.config.duration * this.config.fps);
  }
}

class LimitedCache {
  /** @type {Map<number, HTMLImageElement>} */
  #cache = new Map();

  /** @type {number} */
  #maxSize;
  /** @type {number} */
  #totalFrames;

  /**
   * @param {number} maxSize - 最大缓存大小，如果传过来的是偶数，会+1
   * @param {number} total - 总帧数
   */
  constructor(maxSize, total) {
    this.#totalFrames = total;
    if (maxSize % 2 == 0) {
      this.#maxSize = maxSize + 1;
    } else {
      this.#maxSize = maxSize;
    }
  }

  /**
   * 设置缓存项，自动清理超出范围的帧
   * @param {Number} index - 帧索引
   * @param {HTMLImageElement} img - 图片元素
   * @param {Number} centerIndex - 当前中心帧索引，用于计算有效范围
   */
  set(index, img, centerIndex) {
    this.#cache.set(index, img);
    
    // 如果缓存超过最大尺寸，清理超出范围的帧
    if (this.#cache.size > this.#maxSize) {
      const [start, end] = this.getRangeIndex(centerIndex);
      for (const [cachedIndex] of this.#cache) {
        if (cachedIndex < start || cachedIndex > end) {
          this.#cache.delete(cachedIndex);
        }
      }
    }
  }

  /**
   * @param {Number} index 
   * @returns {HTMLImageElement|null}
   */
  get(index) {
    return this.#cache.get(index) || null;
  }

  /**
   * @param {Number} index 
   * @returns {boolean}
   */
  has(index) {
    return this.#cache.has(index);
  }

  /**
   * 获取基于中心索引的有效缓存范围
   * @param {Number} centerIndex - 中心帧索引
   * @returns {[number, number]} - [起始索引, 结束索引]
   */
  getRangeIndex(centerIndex) {
    const halfSize = Math.floor(this.#maxSize / 2);
    const start = Math.max(0, centerIndex - halfSize);
    const end = Math.min(this.#totalFrames - 1, centerIndex + halfSize);
    return [start, end];
  }

  /**
   * 清空所有缓存
   */
  clear() {
    this.#cache.clear();
  }

  /**
   * 获取当前缓存大小
   * @returns {number}
   */
  get size() {
    return this.#cache.size;
  }
}

/**
 * 帧加载器
 * 负责帧图像的加载、缓存和预加载策略
 */
export class FrameLoader {
  /** @type {LimitedCache} */
  #cache;

  /** @type {Set<number>} */
  #loading = new Set();

  /** @type {ImageSequenceProvider} */
  #provider;

  /** @type {number} */
  #totalFrames;

  /** @type {number} */
  #maxCacheSize;

  /** @type {number} */
  #lastCacheCenter = -1;

  /** @type {number} */
  #lastFrameIndex = 0;

  /**
   * @param {ImageSequenceProvider} provider - 图片序列提供者
   * @param {number} [maxCacheSize=80] - 最大缓存帧数
   */
  constructor(provider, maxCacheSize = 80) {
    this.#provider = provider;
    this.#totalFrames = provider.getTotalFrames();
    this.#maxCacheSize = Math.min(maxCacheSize, this.#totalFrames);
    this.#cache = new LimitedCache(Math.min(maxCacheSize, this.#totalFrames), provider.getTotalFrames());
  }

  /**
   * 触发图片加载（利用浏览器 HTTP 缓存）
   * @param {number} index - 帧索引
   * @returns {Promise<HTMLImageElement|null>}
   */
  #triggerLoad(index) {
    if (this.#cache.has(index)) {
      return Promise.resolve(this.#cache.get(index));
    }

    if (this.#loading.has(index)) {
      return Promise.resolve(null);
    }

    this.#loading.add(index);

    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        const centerIndex = this.#lastCacheCenter >= 0 ? this.#lastCacheCenter : index;
        this.#cache.set(index, img, centerIndex);
        this.#loading.delete(index);
        resolve(img);
      };

      img.onerror = () => {
        this.#loading.delete(index);
        resolve(null);
      };

      img.src = this.#provider.getFrameUrl(index);
    });
  }

  /**
   * 加载所有帧（首次全量加载到浏览器 HTTP 缓存）
   * @returns {Promise<void>}
   */
  async loadAll() {
    const promises = [];
    
    for (let i = 0; i < this.#totalFrames; i++) {
      if (!this.#loading.has(i)) {
        promises.push(this.#triggerLoad(i));
      }
    }

    await Promise.all(promises);
  }

  /**
   * 获取指定帧（如果不在缓存中则立即加载）
   * @param {number} index - 帧索引
   * @returns {HTMLImageElement|null}
   */
  getFrame(index) {
    // 更新最后帧索引状态
    this.#lastFrameIndex = index;

    const cached = this.#cache.get(index);
    
    // 如果缓存命中，直接返回
    if (cached) {
      return cached;
    }

    // 如果未命中，立即触发加载（从浏览器 disk cache 读取很快）
    if (!this.#loading.has(index)) {
      this.#triggerLoad(index);
    }

    return null;
  }

  /**
   * 更新缓存窗口（滑动窗口策略）
   * @param {number} centerIndex - 当前中心帧
   */
  updateCacheWindow(centerIndex) {
    // 如果中心帧变化不大，跳过更新（避免频繁预加载）
    if (Math.abs(centerIndex - this.#lastCacheCenter) < 5) {
      return;
    }

    this.#lastCacheCenter = centerIndex;

    const [start, end] = this.#cache.getRangeIndex(centerIndex);

    // 预加载窗口内的帧（异步，不阻塞）
    this.#preloadWindow(start, end);
  }

  /**
   * 预加载窗口内的帧
   * @param {number} start - 起始帧
   * @param {number} end - 结束帧
   */
  #preloadWindow(start, end) {
    for (let i = start; i <= end; i++) {
      if (!this.#cache.has(i) && !this.#loading.has(i)) {
        this.#triggerLoad(i);
      }
    }
  }

  /**
   * 清理所有缓存释放内存（保留状态）
   */
  clearCache() {
    this.#cache.clear();
    // 注意：不清理 #lastCacheCenter 和 #lastFrameIndex，保留状态
  }

  /**
   * 恢复缓存（基于内部保存的最后帧索引重新加载附近的帧）
   * @param {Number} centerIndex 恢复那一帧范围内的缓存
   */
  resumeCache(centerIndex) {
    const halfSize = Math.floor(this.#maxCacheSize / 2);
    const start = Math.max(0, centerIndex - halfSize);
    const end = Math.min(this.#totalFrames - 1, centerIndex + halfSize);

    // 重置上次缓存中心，强制更新
    this.#lastCacheCenter = -1;
    
    // 重新加载窗口内的帧
    this.#preloadWindow(start, end);
    
    // 更新缓存中心
    this.#lastCacheCenter = centerIndex;
  }

  /**
   * 获取总帧数
   * @returns {number}
   */
  getTotalFrames() {
    return this.#totalFrames;
  }

  /**
   * 销毁加载器，清理缓存和状态
   */
  destroy() {
    this.#cache.clear();
    this.#loading.clear();
    this.#lastCacheCenter = -1;
    this.#lastFrameIndex = 0;
  }
}

/**
 * Canvas 渲染器
 * 负责图片序列在 Canvas 上的渲染，包含平滑插值和 DPR 适配
 */
export class CanvasRenderer {
  /** @type {HTMLCanvasElement|null} */
  #canvas = null;

  /** @type {FrameLoader|null} */
  #frameLoader = null;

  /** @type {number} */
  #lastFrameIndex = -1;

  /** @type {number} */
  #dpr = Math.min(window.devicePixelRatio, 2);

  /** @type {CanvasRenderingContext2D|null} */
  #ctx = null;

  /** @type {{width: number, height: number, offsetX: number, offsetY: number}} */
  #drawParams = { width: 0, height: 0, offsetX: 0, offsetY: 0 };

  #drawParamsInitialized = false;

  /** @type {Function} */
  #inViewCleanup = () => {};

  #initializing = false;

  #startCheckReady = false;

  /**
   * 网页刷新时，会立即触发render，通常此时会绘制失败。
   * 记录失败的绘制帧，然后在所有图片都加载完会，绘制该帧。
   * 这样才能在刷新网页时，能看到当前滚动进度的那一帧的图片。
   */
  #drawFailIndex = -1;

  /**
   * @param {HTMLCanvasElement} canvas - Canvas 元素
   * @param {FrameLoader} frameLoader - 帧加载器
   */
  constructor(canvas, frameLoader) {
    this.#canvas = canvas;
    this.#frameLoader = frameLoader;
  }

  /**
   * 更新绘制参数（保持图片比例）
   * @param {HTMLImageElement} img - 参考图片
   */
  #updateDrawParams(img) {
    if (!this.#canvas) return;

    const imgRatio = img.naturalWidth / img.naturalHeight;
    const canvasRatio = this.#canvas.width / this.#canvas.height;

    if (imgRatio > canvasRatio) {
      // 图片更宽，以高度为基准
      this.#drawParams.height = this.#canvas.height;
      this.#drawParams.width = this.#drawParams.height * imgRatio;
      this.#drawParams.offsetX = (this.#canvas.width - this.#drawParams.width) / 2;
      this.#drawParams.offsetY = 0;
    } else {
      // 图片更高，以宽度为基准
      this.#drawParams.width = this.#canvas.width;
      this.#drawParams.height = this.#drawParams.width / imgRatio;
      this.#drawParams.offsetX = 0;
      this.#drawParams.offsetY = (this.#canvas.height - this.#drawParams.height) / 2;
    }
  }

  /**
   * 绘制指定帧到 Canvas
   * @param {number} frameIndex - 帧索引
   */
  #drawFrame(frameIndex) {
    if (!this.#frameLoader || !this.#ctx) {
      return;
    }

    const img = this.#frameLoader.getFrame(frameIndex);
    if (img) {
      this.#lastFrameIndex = frameIndex;

      if (!this.#drawParamsInitialized) {
        this.#updateDrawParams(img);
        this.#drawParamsInitialized = true;
      }

      this.#ctx.drawImage(
        img,
        this.#drawParams.offsetX,
        this.#drawParams.offsetY,
        this.#drawParams.width,
        this.#drawParams.height
      );
    }
  }

  /**
   * 调整 Canvas 尺寸以匹配 DPR
   */
  resize() {
    if (!this.#canvas) return;

    const rect = this.#canvas.getBoundingClientRect();
    this.#dpr = Math.min(window.devicePixelRatio, 2);

    this.#canvas.width = Math.round(rect.width * this.#dpr);
    this.#canvas.height = Math.round(rect.height * this.#dpr);

    // 重新获取 context（尺寸变化后 context 会失效）
    this.#ctx = this.#canvas.getContext('2d', {
      alpha: false,
      willReadFrequently: false
    });

    // 设置绘制质量
    if (this.#ctx) {
      this.#ctx.imageSmoothingEnabled = true;
      this.#ctx.imageSmoothingQuality = 'high';
    }

    this.#drawParamsInitialized = false;
    this.#drawFrame(this.#lastFrameIndex);
  }

  /**
   * 渲染指定进度的帧（按需渲染，无动画循环）
   * @param {number} progress - 滚动进度 (0-1)
   */
  render(progress) {
    // 限制进度范围
    progress = Math.max(0, Math.min(1, progress));
    // 计算帧索引
    const totalFrames = this.#frameLoader?.getTotalFrames() || 0;
    const frameIndex = Math.min(
      Math.floor(progress * (totalFrames - 1)),
      totalFrames - 1
    );

    if (!this.#canvas || !this.#ctx) {
      this.#drawFailIndex = frameIndex;
      return;
    }
    
    if (frameIndex === this.#lastFrameIndex) {
      return;
    }

    // 更新缓存窗口（异步预加载附近的帧）
    this.#frameLoader?.updateCacheWindow(frameIndex);

    // 绘制帧
    this.#drawFrame(frameIndex);
  }

  /**
   * 检查canvas距离视口一定距离后，才开始加载图片
   * 
   * 使用防抖机制（100ms）避免频繁检查，通过 #initializing 标志位确保只执行一次。
   * 距离计算规则：
   * - 元素在视口上方：距离 = -rect.bottom（视口顶部到元素底部）
   * - 元素在视口下方：距离 = rect.top - viewportHeight（元素顶部到视口底部）
   * - 元素与视口相交：距离 = 0
   * 
   * 当距离小于 preloadDistance（600px）时，触发以下操作：
   * 1. 调用 resize() 初始化 Canvas 尺寸和上下文
   * 2. 加载首帧并立即渲染（快速显示封面）
   * 3. 后台异步加载所有帧（不阻塞首帧显示）
   * 4. 加载完成后移除 scroll 监听器，防止内存泄漏
   * 5. 加载失败会重置状态，但不移除监听器，使其可重新加载
   */
  #checkReady = debounce(() => {
    if (this.#initializing) return;
    const rect = this.#canvas?.getBoundingClientRect();
    if (!rect) return;
    
    const viewportHeight = window.innerHeight;
    const preloadDistance = 600;
    
    // 计算元素距离视口的距离
    let distanceToViewport = 0;
    let elPos = '';
    
    if (rect.bottom < 0) {
      // 元素完全在视口上方：视口顶部 - 元素底部
      distanceToViewport = -rect.bottom;
      elPos = 'top';
    } else if (rect.top > viewportHeight) {
      // 元素完全在视口下方：元素顶部 - 视口底部
      distanceToViewport = rect.top - viewportHeight;
      elPos = 'bottom';
    } else {
      // 元素部分或全部在视口内，距离为 0
      distanceToViewport = 0;
      elPos = 'inner';
    }

    if (distanceToViewport < preloadDistance) {
      this.#initializing = true;
      this.resize();
      this.#frameLoader?.loadAll().then(() => {
        
        this.#checkReady.cancel();
        window.removeEventListener('scroll', this.#checkReady);

        if (this.#frameLoader) {
          let targetFrameIndex;
          if (elPos == 'top') {
            targetFrameIndex = this.#frameLoader.getTotalFrames() - 1 || 0;
          } else if (elPos == 'bottom') {
            targetFrameIndex = 0;
          } else {
            targetFrameIndex = this.#drawFailIndex;
          }
  
          this.#frameLoader.updateCacheWindow(targetFrameIndex);
          setTimeout(() => {
            this.#drawFrame(targetFrameIndex);
          }, 0);
        }
      }).catch(err => {
        this.#initializing = false;
        console.warn('[JxsStVideoFollow] 帧加载失败:', err);
      });
    }
  }, 100);

  /**
   * 启用懒加载机制
   * 
   * 此方法会：
   * 1. 注册 inView 观察者，监听元素进入/离开视口
   *    - 进入视口：停止渲染循环，清理缓存
   *    - 离开视口：恢复缓存（预加载附近的帧）
   * 2. 添加 scroll 事件监听器，检测元素与视口的距离
   *    - 当距离小于 600px 时开始加载图片
   *    - 使用防抖机制避免频繁检查
   *    - 加载完成后自动移除监听器
   * 
   * ⚠️ 重要：必须在创建 CanvasRenderer 后显式调用此方法，否则图片不会自动加载
   * 
   * @example
   * const renderer = new CanvasRenderer(canvas, frameLoader);
   * renderer.waitForReady(); // 启用懒加载
   */
  waitForReady() {
    if (!this.#startCheckReady) {
      this.#startCheckReady = true;
      this.#inViewCleanup = jxsAnimate.inView({
        el: /** @type {Element} */ (this.#canvas),
        once: false,
        enterPercent: 0.1,
        leavePercent: 0.1,
        enterCallback: () => {
          // 进入视口：恢复缓存（预加载附近的帧）
          const centerIndex = this.#lastFrameIndex >= 0 ? this.#lastFrameIndex : 0;
          this.#frameLoader?.resumeCache(centerIndex);
        },
        leaveCallback: () => {
          // 离开视口：清理缓存
          this.#frameLoader?.clearCache();
        },
      });
  
      window.addEventListener('scroll', this.#checkReady, { passive: true });
    }
  }

  /**
   * 销毁渲染器，清理所有资源和监听器
   * 
   * 此方法会执行以下清理操作：
   * 1. 销毁帧加载器（清理缓存和状态）
   * 2. 清理 inView 观察者
   * 3. 取消防抖回调并移除 scroll 事件监听器
   * 4. 清空内部引用（canvas、frameLoader）
   * 
   * ⚠️ 重要：组件卸载时必须调用此方法，防止内存泄漏
   */
  destroy() {
    this.#frameLoader?.destroy();
    this.#canvas = null;
    this.#frameLoader = null;
    this.#inViewCleanup?.();
    this.#checkReady.cancel();
    window.removeEventListener('scroll', this.#checkReady);
    this.#initializing = false;
    this.#startCheckReady = false;
    this.#drawParamsInitialized = false;
    this.#drawFailIndex = -1;
  }
}